import React, { useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { HiArrowRight } from 'react-icons/hi';
import { useLanguage } from '../../contexts/LanguageContext';
import { getProductImageUrl } from '../../utils/imageUtils';
import { getPriceShape, formatCHF } from '../../utils/priceUtils';
import { scrollToTop } from '../../utils/scrollUtils';
import ProductImage from '../common/ProductImage';
import { setClickSource } from '../../services/analytics/analyticsService';

// Offers (badges, sale prices, the deals icon) use the sale colour.
const SALE = 'rgb(var(--sale))';

// A loop group narrower than its viewport would show a gap before the second
// copy scrolls in. Short lists (three live offers, say) are repeated up to this
// many cards so a group always overfills even the widest right half.
const MIN_GROUP_SIZE = 8;
// Seconds each card spends crossing the lane. Duration scales with the group so
// every lane moves at the same speed however many products it carries.
const SECONDS_PER_CARD = 4.5;

const titleOf = (item) => {
  const folder = item?.consumerFolder;
  const fromFolder = Array.isArray(folder) ? folder[0]?.title : folder?.title;
  return fromFolder || item?.description?.longDescription || item?.longDescription || '';
};

// Both feeds are flattened to one card shape here so the lane and card below
// never need to know which API a product came from.
export const normalizeBestseller = (product) => ({
  id: product._id || product.pharmacode,
  pharmacode: product.pharmacode || product._id,
  title: titleOf(product),
  imageUrl: getProductImageUrl(product, 'F'),
  price: getPriceShape(product),
});

// Product-with-articles shape (discount and campaign feeds). Campaign sections
// reuse this without the discount requirement.
export const normalizeArticleProduct = (product, { requireDiscount = false } = {}) => {
  const article = product?.articles?.[0];
  if (!article) return null;
  const price = getPriceShape(article);
  if (requireDiscount && (!price.inDiscount || price.discountPercentage == null)) return null;
  return {
    id: product._id || article.pharmacode,
    pharmacode: article.pharmacode,
    title: titleOf(article),
    imageUrl: getProductImageUrl(article, 'F'),
    price,
  };
};

export const normalizeDeal = (product) => normalizeArticleProduct(product, { requireDiscount: true });

// Exported for the campaign sections further down the page, which show the
// same card in a scroll rail. `accent` lets a campaign tint the sticker/price.
export const ShowcaseCard = ({ item, rank, variant, onSelect, isDuplicate, accent: campaignAccent }) => {
  const isDeal = variant === 'deal';
  const accent = campaignAccent || SALE;
  // Rank numerals: the campaign colour if there is one, otherwise --rank
  // (brand, or the logo's rose on the landing page — set on the span).
  const rankStroke = campaignAccent || 'var(--rank)';
  const { price } = item;

  return (
    <button
      type="button"
      onClick={() => onSelect(item.pharmacode)}
      tabIndex={isDuplicate ? -1 : undefined}
      aria-hidden={isDuplicate || undefined}
      aria-label={item.title}
      className="group/card relative flex h-full flex-shrink-0 flex-col overflow-hidden rounded-[1.4rem] border border-black/[0.05] bg-white p-[clamp(0.5rem,1.1vh,0.85rem)] text-left shadow-[0_18px_40px_-28px_rgba(17,24,39,0.45)] transition-[transform,box-shadow] duration-300 [aspect-ratio:0.76] hover:-translate-y-1 hover:shadow-[0_26px_50px_-26px_rgb(var(--brand)/0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
    >
      {/* Image well */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-[1rem] bg-gradient-to-b from-[#FAF8F6] to-[#F2EFEC]">
        {item.imageUrl ? (
          <ProductImage src={item.imageUrl} alt="" pad={0.12} className="group-hover/card:[--img-hover:1.06]" />
        ) : (
          <svg className="h-8 w-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )}

        {/* Bestsellers carry their chart position as an outlined numeral;
            offers carry a tilted percentage sticker. Same card, two voices. */}
        {!isDeal && rank != null && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-2 top-1 select-none text-[clamp(1.6rem,4.2vh,2.75rem)] font-black leading-none tracking-tighter text-transparent [--rank:rgb(var(--brand))] landing:[--rank:#E33B4C]"
            style={{ WebkitTextStroke: `1.5px ${rankStroke}` }}
          >
            {String(rank).padStart(2, '0')}
          </span>
        )}
        {isDeal && price.discountPercentage != null && (
          <span
            className="absolute right-2 top-2 rotate-[8deg] rounded-full px-2 py-1 text-[clamp(10px,1.35vh,13px)] font-extrabold text-white shadow-[0_8px_18px_-8px_rgb(var(--sale)/0.9)] transition-transform duration-300 group-hover/card:rotate-0"
            style={{ background: accent }}
          >
            -{price.discountPercentage}%
          </span>
        )}
      </div>

      {/* Copy */}
      <p className="mt-[clamp(0.35rem,1vh,0.7rem)] line-clamp-2 text-[clamp(11px,1.45vh,13.5px)] font-semibold leading-snug text-gray-900">
        {item.title}
      </p>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-1.5">
        {price.currentPrice != null ? (
          <>
            <span
              className="text-[clamp(12px,1.6vh,15px)] font-extrabold tracking-tight"
              style={{ color: isDeal || price.inDiscount ? accent : '#111827' }}
            >
              CHF {formatCHF(price.currentPrice)}
            </span>
            {price.inDiscount && price.basePrice != null && price.basePrice > price.currentPrice && (
              <span className="text-[clamp(10px,1.3vh,12px)] text-gray-400 line-through">
                {formatCHF(price.basePrice)}
              </span>
            )}
          </>
        ) : (
          <span className="text-[11px] text-gray-400">&nbsp;</span>
        )}
      </div>
    </button>
  );
};

export const SkeletonCard = () => (
  <div
    aria-hidden="true"
    className="flex h-full flex-shrink-0 flex-col rounded-[1.4rem] border border-black/[0.04] bg-white p-3 [aspect-ratio:0.76]"
  >
    <div className="min-h-0 flex-1 animate-pulse rounded-[1rem] bg-gray-100" />
    <div className="mt-3 h-2.5 w-full animate-pulse rounded bg-gray-100" />
    <div className="mt-2 h-2.5 w-1/2 animate-pulse rounded bg-gray-100" />
  </div>
);

/**
 * One auto-scrolling row of product cards with its own header.
 *
 * Cards are sized off the lane's HEIGHT (h-full + a fixed aspect ratio), not
 * its width: the hero is pinned to the viewport, so height is the scarce axis —
 * a 1366×768 laptop and a 2560×1440 monitor both get two full lanes, the cards
 * simply scale. The viewport is absolutely positioned so that height is
 * definite (a flex-stretched box is not, and h-full would collapse to auto).
 */
const Lane = ({ eyebrow, title, icon, items, loading, variant, reverse, onSelect, onViewAll, viewAllLabel, delay }) => {
  const prefersReduced = useReducedMotion();

  // The first copy of each product is the real, focusable one; padding repeats
  // and the whole second loop group are hidden from AT and the tab order.
  const group = useMemo(() => {
    if (!items.length) return [];
    const out = [];
    for (let i = 0; out.length < Math.max(MIN_GROUP_SIZE, items.length); i++) {
      out.push({ item: items[i % items.length], rank: (i % items.length) + 1, repeat: i >= items.length });
    }
    return out;
  }, [items]);

  const duration = `${Math.max(group.length, 1) * SECONDS_PER_CARD}s`;
  const animated = !prefersReduced && !loading;

  return (
    <motion.section
      className="flex min-h-0 flex-1 flex-col"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      aria-label={title}
    >
      <header className="mb-[clamp(0.5rem,1.4vh,0.9rem)] flex items-center justify-between gap-3 px-1">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className={`inline-flex h-[clamp(1.9rem,4vh,2.4rem)] w-[clamp(1.9rem,4vh,2.4rem)] flex-shrink-0 items-center justify-center rounded-full text-[clamp(0.85rem,1.8vh,1.05rem)] ${
              variant === 'deal' ? 'text-white' : 'bg-white text-brand ring-1 ring-black/[0.06] landing:text-petal'
            }`}
            style={variant === 'deal' ? { background: SALE } : undefined}
            aria-hidden="true"
          >
            {icon}
          </span>
          <div className="min-w-0 leading-tight">
            <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-gray-400">{eyebrow}</span>
            <h2 className="truncate text-[clamp(0.95rem,2.1vh,1.2rem)] font-extrabold tracking-[-0.02em] text-gray-900">{title}</h2>
          </div>
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className="group/all inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-black/[0.07] bg-white px-3.5 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-brand/25 hover:text-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        >
          <span className="hidden xl:inline">{viewAllLabel}</span>
          <span className="xl:hidden">{title}</span>
          <HiArrowRight className="h-3.5 w-3.5 transition-transform group-hover/all:translate-x-0.5" aria-hidden="true" />
        </button>
      </header>

      <div className="relative min-h-0 flex-1">
        <div
          className={`hero-lane absolute inset-0 flex items-center ${animated ? 'overflow-hidden hero-lane-mask' : 'hero-lane-scroll overflow-x-auto'}`}
        >
          <div
            className={`flex h-full max-h-[22rem] w-max items-stretch py-2 ${animated ? 'hero-lane-track' : ''}`}
            style={animated ? { animationDuration: duration, animationDirection: reverse ? 'reverse' : 'normal' } : undefined}
          >
            {loading ? (
              <div className="flex h-full gap-[clamp(0.6rem,1.2vw,1rem)] px-1">
                {[0, 1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)}
              </div>
            ) : (
              [false, true].slice(0, animated ? 2 : 1).map((isCopy) => (
                <div
                  key={isCopy ? 'b' : 'a'}
                  className="flex h-full gap-[clamp(0.6rem,1.2vw,1rem)] pr-[clamp(0.6rem,1.2vw,1rem)]"
                  aria-hidden={isCopy || undefined}
                >
                  {(animated ? group : group.filter((g) => !g.repeat)).map(({ item, rank, repeat }, i) => (
                    <ShowcaseCard
                      key={`${item.id}-${i}`}
                      item={item}
                      rank={rank}
                      variant={variant}
                      onSelect={onSelect}
                      isDuplicate={isCopy || repeat}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </motion.section>
  );
};

/**
 * The right half of the desktop hero: bestsellers on top, live offers below,
 * each an endless lane of product cards. The lanes run in opposite directions
 * so the panel reads as moving without either row feeling like a slideshow.
 *
 * Either lane drops out when its feed is empty and the other takes the full
 * height; Hero hides the whole panel when both are empty.
 */
const HeroShowcase = ({ bestsellers, bestsellersLoading, deals, dealsLoading, onSelect }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const bestsellerItems = useMemo(
    () => bestsellers.map(normalizeBestseller).filter((p) => p.pharmacode),
    [bestsellers]
  );
  const dealItems = useMemo(() => deals.map(normalizeDeal).filter(Boolean), [deals]);

  const go = (path) => {
    navigate(path);
    setTimeout(() => scrollToTop(), 100);
  };

  const showBestsellers = bestsellersLoading || bestsellerItems.length > 0;
  const showDeals = dealsLoading || dealItems.length > 0;

  return (
    <div className="flex h-full flex-col gap-[clamp(0.75rem,2.4vh,1.75rem)]">
      <style>{`
        .hero-lane-track { animation-name: hero-lane-scroll; animation-timing-function: linear; animation-iteration-count: infinite; will-change: transform; }
        .hero-lane:hover .hero-lane-track,
        .hero-lane:focus-within .hero-lane-track { animation-play-state: paused; }
        @keyframes hero-lane-scroll {
          from { transform: translate3d(0, 0, 0); }
          to { transform: translate3d(-50%, 0, 0); }
        }
        .hero-lane-mask {
          -webkit-mask-image: linear-gradient(to right, transparent, #000 6%, #000 94%, transparent);
          mask-image: linear-gradient(to right, transparent, #000 6%, #000 94%, transparent);
        }
        .hero-lane-scroll { scrollbar-width: none; scroll-snap-type: x proximity; }
        .hero-lane-scroll::-webkit-scrollbar { display: none; }
        .hero-lane-scroll button { scroll-snap-align: start; }
      `}</style>

      {showBestsellers && (
        <Lane
          variant="bestseller"
          eyebrow={t('home.bestseller.title')}
          title={t('home.bestseller.titleHighlight')}
          icon="★"
          items={bestsellerItems}
          loading={bestsellersLoading}
          onSelect={(code) => { setClickSource('home_bestsellers'); onSelect(code); }}
          onViewAll={() => go('/products?view=bestseller')}
          viewAllLabel={t('home.bestseller.viewAll')}
          delay={0.15}
        />
      )}

      {showDeals && (
        <Lane
          variant="deal"
          reverse
          eyebrow={t('home.discount.title')}
          title={t('home.discount.titleHighlight')}
          icon="%"
          items={dealItems}
          loading={dealsLoading}
          onSelect={(code) => { setClickSource('home_discounts'); onSelect(code); }}
          onViewAll={() => go('/products?view=discount')}
          viewAllLabel={t('home.discount.viewAll')}
          delay={0.28}
        />
      )}
    </div>
  );
};

export default HeroShowcase;
